import React, { useState, useEffect } from 'react';
import { CONFIG } from '../config';

const HeyDJPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventCode, setEventCode] = useState(null);
  const [publicLink, setPublicLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const fetchRequests = async () => {
    try {
      const resp = await fetch(`${CONFIG.apiBaseUrl}/dj-requests`, {
        credentials: 'include',
      });
      if (!resp.ok) throw new Error('Failed to fetch DJ requests');
      const data = await resp.json();
      setRequests(data.requests || []);
      if (data.event_code) setEventCode(data.event_code);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleRefresh = () => {
    setLoading(true);
    fetchRequests();
  };

  const handleGetPublicLink = async () => {
    setGeneratingLink(true);
    try {
      const resp = await fetch(`${CONFIG.apiBaseUrl}/admin/generate-heydj-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!resp.ok) throw new Error('Failed to generate link');
      const data = await resp.json();
      setPublicLink(`${window.location.origin}/heydj/${data.token}`);
    } catch (err) {
      console.error('Generate link failed:', err.message);
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  const handleStatusChange = async (timestampId, newStatus) => {
    const prev = requests.map(r => ({ ...r }));
    setRequests(requests.map(r =>
      r.timestamp_id === timestampId ? { ...r, status: newStatus } : r
    ));

    try {
      const resp = await fetch(`${CONFIG.apiBaseUrl}/dj-requests`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ timestamp_id: timestampId, status: newStatus }),
      });
      if (!resp.ok) throw new Error('Failed to update status');
    } catch (err) {
      console.error('Status update failed:', err.message);
      setRequests(prev);
    }
  };

  const handleDelete = async (timestampId) => {
    const prev = requests.map(r => ({ ...r }));
    setRequests(requests.filter(r => r.timestamp_id !== timestampId));

    try {
      const resp = await fetch(`${CONFIG.apiBaseUrl}/dj-requests`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ timestamp_id: timestampId }),
      });
      if (!resp.ok) throw new Error('Failed to delete request');
    } catch (err) {
      console.error('Delete failed:', err.message);
      setRequests(prev);
    }
  };

  return (
    <div className="whatsapp-responses-page">
      <div className="responses-header">
        <h2>#HeyDJ Requests</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button onClick={handleRefresh} className="refresh-button" disabled={loading}>
            Refresh
          </button>
          <button onClick={handleGetPublicLink} className="action-button" disabled={generatingLink}>
            {generatingLink ? 'Generating...' : 'Get DJ Link'}
          </button>
          {eventCode && (
            <button onClick={() => setShowQR(!showQR)} className="action-button">
              {showQR ? 'Hide QR Code' : 'Show QR Code'}
            </button>
          )}
        </div>
      </div>

      {publicLink && (
        <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f0fdf4', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', wordBreak: 'break-all' }}>{publicLink}</span>
          <button onClick={handleCopyLink} className="action-button" style={{ fontSize: '12px', padding: '4px 10px' }}>
            {linkCopied ? 'Copied!' : 'Copy'}
          </button>
          <button onClick={() => window.open(publicLink, '_blank')} className="action-button" style={{ fontSize: '12px', padding: '4px 10px' }}>
            Open
          </button>
        </div>
      )}

      {eventCode && showQR && (
        <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#555' }}>Guest Song Request QR Code</h4>
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`${window.location.origin}/heydj/request/${eventCode}`)}`}
            alt="Song Request QR Code"
            style={{ width: '200px', height: '200px', borderRadius: '6px' }}
          />
          <p style={{ fontSize: '12px', color: '#888', margin: '8px 0 0 0' }}>
            Guests scan to submit song requests
          </p>
          <a
            href={`${window.location.origin}/heydj/request/${eventCode}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: '13px', color: '#4f46e5', wordBreak: 'break-all' }}
          >
            {`${window.location.origin}/heydj/request/${eventCode}`}
          </a>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <div className="messages-container">
        {loading ? (
          <div className="loading-text">Loading requests...</div>
        ) : requests.length === 0 ? (
          <p>No DJ requests yet. Guests can text a message containing <strong>#HeyDJ</strong> to submit a request.</p>
        ) : (
          <>
            <div className="messages-count">
              Showing {requests.length} request{requests.length !== 1 ? 's' : ''}
            </div>
            <div className="messages-table-wrapper">
              <table className="messages-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Message</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((req, idx) => (
                    <tr key={req.timestamp_id || idx} className={`dj-row-${req.status || 'unmarked'}`}>
                      <td>{idx + 1}</td>
                      <td>{req.message_body}</td>
                      <td>
                        <div className="dj-status-group">
                          <button
                            className={`dj-status-btn ${req.status === 'unmarked' ? 'active-unmarked' : ''}`}
                            onClick={() => handleStatusChange(req.timestamp_id, 'unmarked')}
                            title="Unmarked"
                          >&#9679;</button>
                          <button
                            className={`dj-status-btn ${req.status === 'approved' ? 'active-approved' : ''}`}
                            onClick={() => handleStatusChange(req.timestamp_id, 'approved')}
                            title="Approved"
                          >&#10003;</button>
                          <button
                            className={`dj-status-btn ${req.status === 'rejected' ? 'active-rejected' : ''}`}
                            onClick={() => handleStatusChange(req.timestamp_id, 'rejected')}
                            title="Rejected"
                          >&#10007;</button>
                        </div>
                      </td>
                      <td>
                        <button
                          className="dj-delete-btn"
                          onClick={() => handleDelete(req.timestamp_id)}
                          title="Delete"
                        >&#128465;</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HeyDJPage;
